import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentMultiConfirmInformationData,
  type EnrollmentMultiConfirmInformationSubmitInput,
  type EnrollmentMultiConfirmInformationSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Multi ConfirmInformation data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiConfirmInformationData>
 * @example const response = await getEnrollmentMultiConfirmInformationData()
 */
export const getEnrollmentMultiConfirmInformationData = async (
  context?: DefaultContext,
): Promise<EnrollmentMultiConfirmInformationData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiConfirmInformationData',
      query: `mutation getEnrollmentMultiConfirmInformationData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
                progress
                eSessionId
                experimentId
                applicationId
                applicationStatus
                firstName
                lastName
                phoneNumber
                email
                addressLine1
                addressLine2
                addressCity
                addressRegionCode
                addressPostalCode
                addressCountry
                maskedSsn
                employmentStatus
                occupation
                isBrokerDealer
                dateOfBirth
                isDirectorPolicyMakerOfCompany
                directorPolicyMakerOfCompany
                investmentProfile {
                    maritalStatus
                    isPoliticallyExposedPerson
                    affiliatedPoliticalOrganizations
                    objective
                    experience
                    taxFilingStatus
                    hasPoliticallyExposedImmediateFamilyMembers
                    politicallyExposedImmediateFamilyMembers
                }
                personalAnnualGrossIncome
                personalNetWorthRange
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiConfirmInformationData }
  return response.submitEnrollment
}

export const useGetEnrollmentMultiConfirmInformationData = createQueryHook<
  undefined,
  EnrollmentMultiConfirmInformationData
>(getEnrollmentMultiConfirmInformationData)

/**
 * Submit Enrollment Multi ConfirmInformation data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiConfirmInformationSubmitOutput>
 * @example const response = await submitEnrollmentMultiConfirmInformationData();
 */
export const submitEnrollmentMultiConfirmInformationData = async (
  enrollmentConfirmInformationSubmitInput: EnrollmentMultiConfirmInformationSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiConfirmInformationSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiConfirmInformationData',
      query: `mutation submitEnrollmentMultiConfirmInformationData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentConfirmInformationSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiConfirmInformationSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiConfirmInformationData = createMutationHook<
  EnrollmentMultiConfirmInformationSubmitInput,
  EnrollmentMultiConfirmInformationSubmitOutput
>(submitEnrollmentMultiConfirmInformationData)
